export default function SEOTextBlock() {
  const scrollToCalculator = () => {
    document.getElementById('roi-calculator')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
          Оценка эффективности обучения персонала: полное руководство
        </h2>
        <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto leading-relaxed">
          Как системно измерять результативность корпоративного обучения сотрудников и доказывать его влияние на бизнес
        </p>

        <div className="prose prose-lg max-w-none text-gray-700 space-y-8">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Почему оценка эффективности корпоративного обучения — это не «анкета после тренинга»</h3>
            <p className="leading-relaxed mb-4">
              Оценка эффективности обучения персонала — это системный процесс измерения того, как корпоративное обучение повлияло на работу сотрудников и бизнес-показатели компании. К сожалению, в большинстве компаний <strong>оценка корпоративного обучения</strong> сводится к анкете «Понравился ли вам тренинг?», которую участники заполняют за 2 минуты до конца занятия. Это измеряет эмоции, а не результат.
            </p>
            <p className="leading-relaxed mb-4">
              Настоящая <strong>оценка эффективности тренинга</strong> начинается задолго до самого тренинга — с фиксации базовых KPI — и продолжается через 1-3 месяца после него, когда новые навыки закрепляются в поведении и начинают влиять на реальные показатели.
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Модель Киркпатрика как основа оценки результатов обучения сотрудников</h3>
            <p className="leading-relaxed mb-4">
              Мировым стандартом <strong>оценки результатов обучения сотрудников</strong> является модель Киркпатрика, разработанная в 1959 году и доработанная в последующие десятилетия. Она выделяет четыре уровня оценки:
            </p>
            <ul className="space-y-2 list-disc pl-6">
              <li><strong>Уровень 1 — Реакция:</strong> как участники отреагировали на обучение, понравился ли им формат, тренер, материалы. Это базовый уровень, который не показывает эффективность, а лишь удовлетворённость.</li>
              <li><strong>Уровень 2 — Знания:</strong> что сотрудники усвоили. Измеряется тестированием до и после обучения. Прирост знаний показывает, насколько хорошо усвоен материал, но не гарантирует его применение.</li>
              <li><strong>Уровень 3 — Поведение:</strong> что изменилось в работе сотрудника. Это ключевой уровень: именно здесь знания превращаются в навыки, навыки — в действия, а действия — в результаты.</li>
              <li><strong>Уровень 4 — Бизнес-результат:</strong> как обучение повлияло на бизнес-показатели — конверсию, средний чек, выручку, прибыль, текучесть, NPS.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">ROI обучения: как посчитать и доказать руководителю</h3>
            <p className="leading-relaxed mb-4">
              <strong>ROI обучения</strong> — это финансовый показатель, который отвечает на главный вопрос собственника: «Сколько мы заработали на каждый вложенный в обучение рубль?» Формула ROI = (Финансовый результат - Затраты на обучение) / Затраты на обучение × 100%.
            </p>
            <p className="leading-relaxed mb-4">
              Практика показывает, что грамотно выстроенное корпоративное обучение даёт ROI от 200% до 600%. Обучение отдела продаж окупается быстрее всего — рост конверсии на 7-15 процентных пунктов даёт многократную отдачу. Обучение руководителей окупается медленнее, но эффект более системный — рост производительности целых отделов на 15-25%.
            </p>

            <div className="mt-6 bg-teal-50 rounded-2xl p-6 border border-teal-100 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-calculator-line text-xl text-white"></i>
                </div>
                <p className="text-gray-800 font-semibold leading-snug">
                  Посчитайте ROI и покажите руководителю полноценный отчёт
                </p>
              </div>
              <button
                type="button"
                onClick={scrollToCalculator}
                className="w-full md:w-auto bg-teal-600 text-white px-7 py-3.5 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 flex-shrink-0"
              >
                Калькулятор ROI
                <i className="ri-arrow-up-line"></i>
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">KPI обучения персонала: что измерять для разных должностей</h3>
            <p className="leading-relaxed mb-4">
              Выбор <strong>показателей эффективности обучения</strong> зависит от должности и целей обучения. Для отдела продаж ключевые метрики: конверсия, средний чек, длина сделки, количество повторных продаж. Для сервисного отдела: CSI, NPS, количество жалоб, скорость обработки обращений. Для руководителей: выполнение KPI подчинёнными, текучесть в отделе, скорость принятия решений, вовлечённость команды.
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Анализ эффективности обучения: системный подход</h3>
            <p className="leading-relaxed mb-4">
              <strong>Анализ эффективности обучения</strong> требует системного подхода, а не разовых замеров. Он включает: диагностику текущих показателей (точка А), постановку измеримых целей (точка Б), проведение обучения с практикой и сопровождением, повторный замер через 1-3 месяца и расчёт эффекта как разницы между точкой Б и точкой А.
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Корпоративное обучение персонала в Беларуси: как оценивают компании</h3>
            <p className="leading-relaxed mb-4">
              <strong>Корпоративное обучение персонала</strong> в Беларуси активно развивается. Компании Минска и регионов всё чаще переходят от разовых тренингов к системному <strong>развитию сотрудников</strong>. При этом запрос на <strong>оценку эффективности корпоративных тренингов</strong> растёт: руководители хотят видеть цифры, а не просто сертификаты участников.
            </p>
            <p className="leading-relaxed mb-4">
              <strong>Корпоративные тренинги в Минске</strong> сегодня — это не только обучение, но и система измерения результатов. Компании, которые внедряют оценку по модели Киркпатрика, получают в 3-5 раз больше отдачи от обучения. Ключ к успеху — не в выборе «правильного» тренинга, а в выстраивании системы: диагностика → цели → замер → обучение → практика → повторный замер → отчёт.
            </p>
          </div>

          <div className="bg-teal-50 rounded-2xl p-8 border border-teal-100">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Ключевые выводы</h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0 mt-2"></span>
                <span className="text-gray-700">Оценка эффективности обучения — это система, а не разовая анкета</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0 mt-2"></span>
                <span className="text-gray-700">Модель Киркпатрика даёт проверенную структуру для оценки на четырёх уровнях</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0 mt-2"></span>
                <span className="text-gray-700">Без замера до обучения невозможно доказать эффективность</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0 mt-2"></span>
                <span className="text-gray-700">ROI обучения в 200-600% — реальность для компаний, которые внедряют системный подход</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-teal-600 rounded-full flex-shrink-0 mt-2"></span>
                <span className="text-gray-700">Без сопровождения после обучения 80-90% знаний теряется за 2 недели</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}